import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import api from "../services/api";
import V2Tickets, { V2TicketDetail } from "./V2Tickets";
import V2AiAnalytics from "./V2AiAnalytics";
import V2TerminalDiagnostics from "./V2TerminalDiagnostics";
import V2AssistantReview from "./V2AssistantReview";
import {
    V2Admin,
    V2Analytics,
    V2Contacts,
    V2KnowledgeBase,
    V2Omni,
    V2Reports,
} from "./V2Modules";
import "./V2App.css";

const SupportV2Context = createContext(null);

export function useSupportV2() {
    return useContext(SupportV2Context);
}

const NAVIGATION = [
    { code: "tickets", label: "Заявки", path: "/v2/tickets", icon: "tickets", permission: "tickets.read" },
    { code: "telephony", label: "Телефония", icon: "phone", overlay: true, permission: "tickets.read" },
    { code: "omni", label: "Omni", path: "/v2/omni", icon: "omni", permission: "omni.read" },
    { code: "contacts", label: "Контакты", path: "/v2/contacts/users", icon: "contacts", permission: "contacts.read" },
    { code: "knowledge_base", label: "База знаний", path: "/v2/knowledge-base", icon: "book", permission: "knowledge_base.read" },
    { code: "reports", label: "Отчёты", path: "/v2/reports", icon: "reports", permission: "reports.read" },
    { code: "ai_analytics", feature: "analytics", label: "ИИ Аналитика", path: "/v2/ai-analytics", icon: "sparkles", permission: "analytics.read" },
    { code: "terminal_diagnostics", label: "Терминалы", path: "/v2/terminal-diagnostics", icon: "monitor", terminalDiagnostics: true },
    { code: "assistant_review", label: "Разбор ИИ", path: "/v2/assistant-review", icon: "sparkles", assistantReview: true },
    { code: "analytics", label: "Аналитика", path: "/v2/analytics", icon: "analytics", permission: "analytics.read" },
    { code: "admin", label: "Управление", path: "/v2/admin/general", icon: "settings", admin: true },
];

const CONTEXT_SECTIONS = {
    assistant_review: [{ label: "Ответы и исправления", path: "/v2/assistant-review" }],
    terminal_diagnostics: [{ label: "Диагностика Hermes", path: "/v2/terminal-diagnostics" }],
    tickets: [
        { label: "Входящие", path: "/v2/tickets?queue=incoming", count: "incoming" },
        { label: "Мои", path: "/v2/tickets?queue=mine" },
        { label: "Неприсвоенные", path: "/v2/tickets?queue=unassigned", count: "unassigned" },
        { label: "Контролируемые", path: "/v2/tickets?queue=controlled" },
        { label: "Выполненные", path: "/v2/tickets?queue=closed", count: "closed" },
        { label: "Замороженные", path: "/v2/tickets?queue=frozen" },
        { label: "Корзина", path: "/v2/tickets?queue=trash", count: "trash" },
    ],
    contacts: [
        { label: "Пользователи", path: "/v2/contacts/users" },
        { label: "Компании", path: "/v2/contacts/companies" },
    ],
    knowledge_base: [
        { label: "Все статьи", path: "/v2/knowledge-base" },
        { label: "Опубликованные", path: "/v2/knowledge-base?status=published" },
        { label: "Черновики", path: "/v2/knowledge-base?status=draft" },
    ],
    reports: [
        { label: "Каталог отчётов", path: "/v2/reports" },
        { label: "ИИ Аналитика", path: "/v2/ai-analytics" },
        { label: "Аналитика", path: "/v2/analytics" },
    ],
    admin: [
        { label: "Общие настройки", path: "/v2/admin/general" },
        { label: "Права и группы", path: "/v2/admin/access-groups" },
        { label: "Каналы", path: "/v2/admin/channels" },
        { label: "Поля", path: "/v2/admin/custom-fields" },
        { label: "Диспетчер", path: "/v2/admin/automation-rules" },
        { label: "Планировщик", path: "/v2/admin/schedules" },
        { label: "Журнал автоматизации", path: "/v2/admin/automation-runs" },
        { label: "Учёт времени", path: "/v2/admin/time-entries" },
    ],
};

export function V2Icon({ name, size = 22 }) {
    const paths = {
        monitor: <><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4M7 10h3l2-3 2 6 2-3h2"/></>,
        tickets: <><path d="M4 5h16v12H8l-4 3V5Z"/><path d="M8 9h8M8 13h5"/></>,
        phone: <path d="M7 3h3l1.2 4-2 1.5a15 15 0 0 0 6.3 6.3l1.5-2L21 14v3c0 2-1.5 4-4 4C9 20 4 15 3 7c0-2.5 2-4 4-4Z"/>,
        omni: <><circle cx="12" cy="12" r="8"/><path d="M4 12h16M12 4c2 2.5 3 5.2 3 8s-1 5.5-3 8c-2-2.5-3-5.2-3-8s1-5.5 3-8Z"/></>,
        contacts: <><circle cx="9" cy="8" r="3"/><path d="M3.5 20c.5-4 2.5-6 5.5-6s5 2 5.5 6M16 5h5M18.5 2.5v5"/></>,
        book: <><path d="M4 4h6a3 3 0 0 1 3 3v13a3 3 0 0 0-3-3H4V4Z"/><path d="M20 4h-4a3 3 0 0 0-3 3v13a3 3 0 0 1 3-3h4V4Z"/></>,
        reports: <><path d="M5 3h11l3 3v15H5V3Z"/><path d="M8 16v-3M12 16V9M16 16v-5"/></>,
        analytics: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></>,
        sparkles: <><path d="m12 3 1.25 3.75L17 8l-3.75 1.25L12 13l-1.25-3.75L7 8l3.75-1.25L12 3Z"/><path d="m18.5 14 .75 2.25L21.5 17l-2.25.75L18.5 20l-.75-2.25L15.5 17l2.25-.75.75-2.25ZM5.5 12l.65 1.85L8 14.5l-1.85.65L5.5 17l-.65-1.85L3 14.5l1.85-.65L5.5 12Z"/></>,
        refresh: <><path d="M20 11a8 8 0 1 0-2.3 6"/><path d="M20 5v6h-6"/></>,
        settings: <><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1"/></>,
        search: <><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></>,
        plus: <path d="M12 5v14M5 12h14"/>,
        download: <><path d="M12 3v12M7 10l5 5 5-5"/><path d="M5 21h14"/></>,
        menu: <path d="M4 7h16M4 12h16M4 17h16"/>,
        close: <path d="m6 6 12 12M18 6 6 18"/>,
        user: <><circle cx="12" cy="8" r="4"/><path d="M4 21c.7-5 3.3-7 8-7s7.3 2 8 7"/></>,
        chevron: <path d="m9 6 6 6-6 6"/>,
    };
    return (
        <svg className="v2-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{paths[name] || paths.tickets}</g>
        </svg>
    );
}

function resolveSection(pathname) {
    if (pathname.includes("/assistant-review")) return "assistant_review";
    if (pathname.includes("/terminal-diagnostics")) return "terminal_diagnostics";
    if (pathname.includes("/contacts")) return "contacts";
    if (pathname.includes("/knowledge-base")) return "knowledge_base";
    if (pathname.includes("/reports") || pathname.includes("/analytics")) return "reports";
    if (pathname.includes("/admin")) return "admin";
    if (pathname.includes("/omni")) return "omni";
    return "tickets";
}

function Shell({ bootstrap, refreshBootstrap, children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileNav, setMobileNav] = useState(false);
    const [telephony, setTelephony] = useState(false);
    const [terminalAttention, setTerminalAttention] = useState({ count: 0, items: [] });
    useEffect(() => {
        if (!bootstrap.terminalDiagnostics) return undefined;
        let active = true;
        const check = async () => { if (document.visibilityState !== 'visible') return; try { const { data } = await api.get('/api/v2/terminal-diagnostics/attention'); if (active) setTerminalAttention(data); } catch { /* Keep the last known escalation count. */ } };
        check(); const timer = window.setInterval(check, 60000); window.addEventListener('terminal-attention-changed', check);
        return () => { active = false; window.clearInterval(timer); window.removeEventListener('terminal-attention-changed', check); };
    }, [bootstrap.terminalDiagnostics]);
    const section = resolveSection(location.pathname);
    const features = useMemo(() => Object.fromEntries((bootstrap.features || []).map((item) => [item.code, item.enabled])), [bootstrap.features]);
    const permissions = bootstrap.permissions || [];
    const isAdmin = permissions.includes("*");
    const can = (permission) => !permission || isAdmin || permissions.includes(permission) || permissions.includes(`${permission.split(".")[0]}.*`);
    const context = CONTEXT_SECTIONS[section] || [];

    const isContextActive = (path) => {
        const [pathname, search = ""] = path.split("?");
        if (location.pathname !== pathname) return false;
        const expected = new URLSearchParams(search);
        const current = new URLSearchParams(location.search);
        const relevantKeys = new Set(context.flatMap((item) => Array.from(new URLSearchParams(item.path.split("?")[1] || "").keys())));
        return Array.from(relevantKeys).every((key) => {
            const currentValue = key === "queue" && section === "tickets" ? (current.get(key) || "incoming") : (current.get(key) || "");
            return currentValue === (expected.get(key) || "");
        });
    };

    return (
        <div className="support-v2">
            <aside className={`v2-rail ${mobileNav ? "is-open" : ""}`}>
                <button className="v2-brand" onClick={() => navigate("/v2/tickets")} aria-label="Чио Чио Support">
                    <span>Ч</span><small>support</small>
                </button>
                <nav className="v2-main-nav">
                    {NAVIGATION.filter((item) => features[item.feature || item.code] !== false && can(item.permission) && (!item.admin || isAdmin) && (!item.terminalDiagnostics || bootstrap.terminalDiagnostics === true) && (!item.assistantReview || bootstrap.assistantReview === true)).map((item) => item.overlay ? (
                        <button key={item.code} className="v2-nav-item" onClick={() => { setTelephony(true); setMobileNav(false); }} title={item.label}>
                            <V2Icon name={item.icon}/><span>{item.label}</span>
                        </button>
                    ) : (
                        <NavLink key={item.code} to={item.path} className={({ isActive }) => `v2-nav-item ${isActive ? "active" : ""}`} onClick={() => setMobileNav(false)} title={item.label}>
                            <V2Icon name={item.icon}/><span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <NavLink to={isAdmin ? "/v2/admin/general" : "/profile"} className="v2-profile" title={bootstrap.user?.name || "Профиль"}>
                    <V2Icon name="user" size={19}/>
                </NavLink>
            </aside>

            <aside className={`v2-context ${mobileNav ? "is-open" : ""}`}>
                <div className="v2-context-title">
                    <strong>{NAVIGATION.find((item) => item.code === section)?.label || "Support v2"}</strong>
                    <button className="v2-mobile-close" onClick={() => setMobileNav(false)}><V2Icon name="close"/></button>
                </div>
                <nav>
                    {context.map((item) => {
                        const count = item.count === "incoming" ? (bootstrap.counters?.incoming ?? bootstrap.counters?.total) : bootstrap.counters?.[item.count];
                        return <button key={item.path} className={isContextActive(item.path) ? "active" : ""} onClick={() => { navigate(item.path); setMobileNav(false); }}>
                            <span>{item.label}</span>
                            {item.count && count !== undefined ? <b>{count}</b> : null}
                        </button>;
                    })}
                </nav>
                <div className="v2-context-foot">
                    <span>Support v2</span>
                    <small>единая база с v1</small>
                </div>
            </aside>

            <section className="v2-workspace">
                <header className="v2-topbar">
                    <button className="v2-menu-button" onClick={() => setMobileNav(true)}><V2Icon name="menu"/></button>
                    <div className="v2-topbar-search">
                        <V2Icon name="search" size={18}/>
                        <input aria-label="Глобальный поиск" placeholder="Поиск по заявкам, контактам и статьям" onKeyDown={(event) => {
                            if (event.key === "Enter" && event.currentTarget.value.trim()) navigate(`/v2/tickets?q=${encodeURIComponent(event.currentTarget.value.trim())}`);
                        }}/>
                    </div>
                    {bootstrap.terminalDiagnostics && terminalAttention.count > 0 ? <button className="tc-global-alert" aria-label={`Терминалам нужна помощь: ${terminalAttention.count}`} onClick={() => navigate(`/v2/terminal-diagnostics?terminal=${encodeURIComponent(terminalAttention.items?.[0]?.terminal || '')}`)}><V2Icon name="monitor" size={16}/><span>Нужна помощь</span><b>{terminalAttention.count}</b></button> : null}
                    <div className="v2-user-chip">
                        <span>{(bootstrap.user?.name || "С").slice(0, 1).toUpperCase()}</span>
                        <div><b>{bootstrap.user?.name || "Сотрудник"}</b><small>{isAdmin ? "Администратор" : "Поддержка"}</small></div>
                    </div>
                </header>
                <main className="v2-main">{children}</main>
            </section>

            {mobileNav ? <button className="v2-backdrop" aria-label="Закрыть меню" onClick={() => setMobileNav(false)}/> : null}
            {telephony ? (
                <div className="v2-overlay" role="dialog" aria-modal="true" aria-label="Телефония">
                    <div className="v2-call-panel">
                        <header><div><V2Icon name="phone"/><strong>Телефония</strong></div><button onClick={() => setTelephony(false)}><V2Icon name="close"/></button></header>
                        <div className="v2-empty v2-call-empty"><span className="v2-empty-icon"><V2Icon name="phone" size={30}/></span><h3>Нет вызовов</h3><p>Провайдер телефонии пока не выбран. Интерфейс адаптера готов к подключению.</p></div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default function SupportV2App() {
    const [state, setState] = useState({ loading: true, data: null, error: "" });
    const load = async () => {
        setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const { data } = await api.get("/api/v2/bootstrap");
            setState({ loading: false, data, error: "" });
        } catch (error) {
            setState({ loading: false, data: null, error: error?.response?.data?.error?.message || "Не удалось открыть Support v2" });
        }
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!state.data?.enabled || !process.env.REACT_APP_SUPPORT_WS_URL) return undefined;
        let socket;
        let retryTimer;
        let stopped = false;
        const connect = async () => {
            try {
                const { data } = await api.post("/api/v2/realtime/token");
                if (stopped) return;
                const url = new URL(process.env.REACT_APP_SUPPORT_WS_URL);
                url.searchParams.set("token", data.token);
                socket = new WebSocket(url.toString());
                socket.onmessage = (event) => {
                    try { window.dispatchEvent(new CustomEvent("support-v2:realtime", { detail: JSON.parse(event.data) })); }
                    catch { /* ignore malformed external frames */ }
                };
                socket.onclose = () => { if (!stopped) retryTimer = window.setTimeout(connect, 5000); };
            } catch { if (!stopped) retryTimer = window.setTimeout(connect, 10000); }
        };
        connect();
        return () => { stopped = true; window.clearTimeout(retryTimer); socket?.close(); };
    }, [state.data?.enabled]);

    if (state.loading) return <div className="support-v2 v2-gate"><div className="v2-spinner"/><span>Загружаем Support v2…</span></div>;
    if (state.error || !state.data?.enabled) return (
        <div className="support-v2 v2-gate"><div className="v2-gate-card"><h1>Support v2 недоступен</h1><p>{state.error || "Модуль выключен feature flag."}</p><button className="v2-button primary" onClick={load}>Повторить</button><a href="/">Вернуться в v1</a></div></div>
    );

    return (
        <SupportV2Context.Provider value={{ bootstrap: state.data, refreshBootstrap: load }}>
            <Shell bootstrap={state.data} refreshBootstrap={load}>
                <Routes>
                    <Route index element={<Navigate to="tickets" replace/>}/>
                    <Route path="tickets" element={<V2Tickets/>}/>
                    <Route path="tickets/:id" element={<V2TicketDetail/>}/>
                    <Route path="omni" element={<V2Omni/>}/>
                    <Route path="contacts/users" element={<V2Contacts type="users"/>}/>
                    <Route path="contacts/companies" element={<V2Contacts type="companies"/>}/>
                    <Route path="knowledge-base" element={<V2KnowledgeBase/>}/>
                    <Route path="reports" element={<V2Reports/>}/>
                    <Route path="ai-analytics" element={<V2AiAnalytics/>}/>
                    <Route path="terminal-diagnostics" element={<V2TerminalDiagnostics/>}/>
                    <Route path="assistant-review" element={<V2AssistantReview/>}/>
                    <Route path="analytics" element={<V2Analytics/>}/>
                    <Route path="admin/:section" element={<V2Admin/>}/>
                    <Route path="*" element={<Navigate to="tickets" replace/>}/>
                </Routes>
            </Shell>
        </SupportV2Context.Provider>
    );
}
